/**
 * PR-29A — Poll option autofix.
 * Guarantees binary (Sí/No) polls always render both options even when
 * upstream data is missing one. Fires `poll_missing_option_autofixed`
 * when a fix has to be applied.
 */
import type { PollOption, PollWithOptions } from "@/lib/polls/pollsService";
import { trackEvent } from "@/lib/audit";

const YES = ["sí", "si", "yes", "sim"];
const NO = ["no", "não", "nao"];

function isYes(label: string) { return YES.includes(label.trim().toLowerCase()); }
function isNo(label: string) { return NO.includes(label.trim().toLowerCase()); }

/**
 * If the poll looks binary (yes/no) and is missing one option, append the
 * complementary option synthesized client-side so the user can always vote.
 */
export function ensureBinaryOptions(poll: PollWithOptions): PollWithOptions {
  if (!poll || !poll.options) return poll;
  const opts = poll.options;
  const hasYes = opts.some((o) => isYes(o.label));
  const hasNo = opts.some((o) => isNo(o.label));
  const looksBinary = (hasYes || hasNo) && opts.length < 2;
  if (!looksBinary) return poll;
  const missing: PollOption = hasYes
    ? { id: `${poll.id}_auto_no`, poll_id: poll.id, label: "No", sort_order: (opts[0]?.sort_order ?? 0) + 1 }
    : { id: `${poll.id}_auto_si`, poll_id: poll.id, label: "Sí", sort_order: -1 };
  void trackEvent("poll_missing_option_autofixed", { poll_id: poll.id, added: missing.label });
  const next = hasYes ? [...opts, missing] : [missing, ...opts];
  return { ...poll, options: next };
}